import { z } from 'zod';

import type {
  CreateRegulatoryDirectoryEntityRequest,
  CreateRegulatoryDirectoryEntityResponse,
  DeactivateRegulatoryDirectoryEntityRequest,
  DeactivateRegulatoryDirectoryEntityResponse,
  GetRegulatoryDirectoryCandidateResponse,
  GetRegulatoryDirectoryEntityResponse,
  ListRegulatoryDirectoryAuditEntriesRequest,
  ListRegulatoryDirectoryAuditEntriesResponse,
  ListRegulatoryDirectoryCandidatesRequest,
  ListRegulatoryDirectoryCandidatesResponse,
  ListRegulatoryDirectoryEntitiesRequest,
  ListRegulatoryDirectoryEntitiesResponse,
  MergeRegulatoryDirectoryEntitiesRequest,
  MergeRegulatoryDirectoryEntitiesResponse,
  PromoteRegulatoryDirectoryCandidateRequest,
  PromoteRegulatoryDirectoryCandidateResponse,
  ReactivateRegulatoryDirectoryEntityRequest,
  ReactivateRegulatoryDirectoryEntityResponse,
  RegulatoryDirectoryAuditAction,
  RegulatoryDirectoryAuditEntryRecord,
  RegulatoryDirectoryCandidateDetail,
  RegulatoryDirectoryCandidateRecord,
  RegulatoryDirectoryCandidateSourceRecord,
  RegulatoryDirectoryCandidateSourceType,
  RegulatoryDirectoryCandidateStatus,
  RegulatoryDirectoryEntityDetail,
  RegulatoryDirectoryEntitySummary,
  RegulatoryEntityAliasRecord,
  RejectRegulatoryDirectoryCandidateRequest,
  RejectRegulatoryDirectoryCandidateResponse,
  ResolveRegulatoryDirectoryCandidateRequest,
  ResolveRegulatoryDirectoryCandidateResponse,
  UpdateRegulatoryDirectoryEntityRequest,
  UpdateRegulatoryDirectoryEntityResponse,
  VerifyRegulatoryDirectoryEntityRequest,
  VerifyRegulatoryDirectoryEntityResponse,
} from './dto.js';
import {
  regulatoryDirectoryAuditActionValues,
  regulatoryDirectoryCandidateSourceTypeValues,
  regulatoryDirectoryCandidateStatusValues,
} from './dto.js';
import {
  RegulatoryEntityRecordSchema,
  RegulatoryEntityVerificationStatusSchema,
  RegulatoryIdentityKindSchema,
  RegulatoryIdentitySchema,
} from '../counterparties/counterparties.schema.js';
import { CompanyReceiverTypeSchema } from '../shipments/party.schema.js';

const trimmedStringSchema = z.string().trim().min(1);
const timestampSchema = z.iso.datetime();
const limitSchema = z.number().int().min(1).max(100).optional();

export const RegulatoryDirectoryCandidateStatusSchema = z.enum(
  regulatoryDirectoryCandidateStatusValues,
) satisfies z.ZodType<RegulatoryDirectoryCandidateStatus>;

export const RegulatoryDirectoryCandidateSourceTypeSchema = z.enum(
  regulatoryDirectoryCandidateSourceTypeValues,
) satisfies z.ZodType<RegulatoryDirectoryCandidateSourceType>;

export const RegulatoryDirectoryAuditActionSchema = z.enum(
  regulatoryDirectoryAuditActionValues,
) satisfies z.ZodType<RegulatoryDirectoryAuditAction>;

export const RegulatoryEntityAliasRecordSchema = z
  .object({
    id: z.uuid(),
    value: trimmedStringSchema,
    normalizedValue: trimmedStringSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strict() satisfies z.ZodType<RegulatoryEntityAliasRecord>;

export const RegulatoryDirectoryAuditEntryRecordSchema = z
  .object({
    id: z.uuid(),
    regulatoryEntityId: z.uuid().nullable().optional(),
    candidateId: z.uuid().nullable().optional(),
    actorUserId: z.uuid(),
    action: RegulatoryDirectoryAuditActionSchema,
    reason: trimmedStringSchema.nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).nullable().optional(),
    occurredAt: timestampSchema,
    createdAt: timestampSchema,
  })
  .strict() satisfies z.ZodType<RegulatoryDirectoryAuditEntryRecord>;

export const RegulatoryDirectoryCandidateSourceRecordSchema = z
  .object({
    id: z.uuid(),
    candidateId: z.uuid(),
    sourceType: RegulatoryDirectoryCandidateSourceTypeSchema,
    sourceCompanyId: z.uuid(),
    sourceRecordId: z.uuid(),
    sourceDisplayName: trimmedStringSchema,
    receiverType: CompanyReceiverTypeSchema.nullable().optional(),
    referenceCode: trimmedStringSchema.nullable().optional(),
    addressLine1: trimmedStringSchema.nullable().optional(),
    addressLine2: trimmedStringSchema.nullable().optional(),
    city: trimmedStringSchema.nullable().optional(),
    state: trimmedStringSchema.nullable().optional(),
    postalCode: trimmedStringSchema.nullable().optional(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    lastObservedAt: timestampSchema,
  })
  .strict() satisfies z.ZodType<RegulatoryDirectoryCandidateSourceRecord>;

export const RegulatoryDirectoryEntitySummarySchema = RegulatoryEntityRecordSchema.extend({
  mergedIntoRegulatoryEntityId: z.uuid().nullable().optional(),
  aliasCount: z.number().int().nonnegative(),
  linkedCompanyReceiverCount: z.number().int().nonnegative(),
  linkedLocationCount: z.number().int().nonnegative(),
  linkedInternalLocationCount: z.number().int().nonnegative(),
}).strict() satisfies z.ZodType<RegulatoryDirectoryEntitySummary>;

export const RegulatoryDirectoryEntityDetailSchema = RegulatoryDirectoryEntitySummarySchema.extend({
  aliases: z.array(RegulatoryEntityAliasRecordSchema),
}).strict() satisfies z.ZodType<RegulatoryDirectoryEntityDetail>;

export const RegulatoryDirectoryCandidateRecordSchema = z
  .object({
    id: z.uuid(),
    identity: RegulatoryIdentitySchema,
    displayName: trimmedStringSchema,
    receiverType: CompanyReceiverTypeSchema.nullable().optional(),
    addressLine1: trimmedStringSchema.nullable().optional(),
    addressLine2: trimmedStringSchema.nullable().optional(),
    city: trimmedStringSchema.nullable().optional(),
    state: trimmedStringSchema.nullable().optional(),
    postalCode: trimmedStringSchema.nullable().optional(),
    status: RegulatoryDirectoryCandidateStatusSchema,
    resolvedRegulatoryEntityId: z.uuid().nullable().optional(),
    rejectedReason: trimmedStringSchema.nullable().optional(),
    sourceCount: z.number().int().nonnegative(),
    lastObservedAt: timestampSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strict() satisfies z.ZodType<RegulatoryDirectoryCandidateRecord>;

export const RegulatoryDirectoryCandidateDetailSchema =
  RegulatoryDirectoryCandidateRecordSchema.extend({
    sources: z.array(RegulatoryDirectoryCandidateSourceRecordSchema),
  }).strict() satisfies z.ZodType<RegulatoryDirectoryCandidateDetail>;

export const ListRegulatoryDirectoryEntitiesRequestSchema = z
  .object({
    query: z.string().trim().optional(),
    kind: RegulatoryIdentityKindSchema.optional(),
    verificationStatus: RegulatoryEntityVerificationStatusSchema.optional(),
    receiverType: CompanyReceiverTypeSchema.nullable().optional(),
    limit: limitSchema,
  })
  .strict() satisfies z.ZodType<ListRegulatoryDirectoryEntitiesRequest>;

export const ListRegulatoryDirectoryEntitiesResponseSchema = z
  .object({
    items: z.array(RegulatoryDirectoryEntitySummarySchema),
    total: z.number().int().nonnegative(),
  })
  .strict() satisfies z.ZodType<ListRegulatoryDirectoryEntitiesResponse>;

export const GetRegulatoryDirectoryEntityResponseSchema = z
  .object({
    entity: RegulatoryDirectoryEntityDetailSchema,
  })
  .strict() satisfies z.ZodType<GetRegulatoryDirectoryEntityResponse>;

export const CreateRegulatoryDirectoryEntityRequestSchema = z
  .object({
    identity: RegulatoryIdentitySchema,
    displayName: trimmedStringSchema,
    receiverType: CompanyReceiverTypeSchema.nullable().optional(),
    addressLine1: trimmedStringSchema.nullable().optional(),
    addressLine2: trimmedStringSchema.nullable().optional(),
    city: trimmedStringSchema.nullable().optional(),
    state: trimmedStringSchema.nullable().optional(),
    postalCode: trimmedStringSchema.nullable().optional(),
    aliasValues: z.array(trimmedStringSchema).optional(),
  })
  .strict() satisfies z.ZodType<CreateRegulatoryDirectoryEntityRequest>;

export const CreateRegulatoryDirectoryEntityResponseSchema = z
  .object({
    entity: RegulatoryDirectoryEntityDetailSchema,
  })
  .strict() satisfies z.ZodType<CreateRegulatoryDirectoryEntityResponse>;

export const UpdateRegulatoryDirectoryEntityRequestSchema = z
  .object({
    entityId: z.uuid(),
    displayName: trimmedStringSchema.optional(),
    receiverType: CompanyReceiverTypeSchema.nullable().optional(),
    addressLine1: trimmedStringSchema.nullable().optional(),
    addressLine2: trimmedStringSchema.nullable().optional(),
    city: trimmedStringSchema.nullable().optional(),
    state: trimmedStringSchema.nullable().optional(),
    postalCode: trimmedStringSchema.nullable().optional(),
    aliasValues: z.array(trimmedStringSchema).optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.displayName !== undefined
      || value.receiverType !== undefined
      || value.addressLine1 !== undefined
      || value.addressLine2 !== undefined
      || value.city !== undefined
      || value.state !== undefined
      || value.postalCode !== undefined
      || value.aliasValues !== undefined,
    { message: 'At least one field must be provided to update regulatory directory entity' },
  ) satisfies z.ZodType<UpdateRegulatoryDirectoryEntityRequest>;

export const UpdateRegulatoryDirectoryEntityResponseSchema = z
  .object({
    entity: RegulatoryDirectoryEntityDetailSchema,
  })
  .strict() satisfies z.ZodType<UpdateRegulatoryDirectoryEntityResponse>;

export const VerifyRegulatoryDirectoryEntityRequestSchema = z
  .object({
    entityId: z.uuid(),
    reason: trimmedStringSchema.nullable().optional(),
  })
  .strict() satisfies z.ZodType<VerifyRegulatoryDirectoryEntityRequest>;

export const VerifyRegulatoryDirectoryEntityResponseSchema = z
  .object({
    entity: RegulatoryDirectoryEntityDetailSchema,
  })
  .strict() satisfies z.ZodType<VerifyRegulatoryDirectoryEntityResponse>;

export const DeactivateRegulatoryDirectoryEntityRequestSchema = z
  .object({
    entityId: z.uuid(),
    reason: trimmedStringSchema.nullable().optional(),
  })
  .strict() satisfies z.ZodType<DeactivateRegulatoryDirectoryEntityRequest>;

export const DeactivateRegulatoryDirectoryEntityResponseSchema = z
  .object({
    entity: RegulatoryDirectoryEntityDetailSchema,
  })
  .strict() satisfies z.ZodType<DeactivateRegulatoryDirectoryEntityResponse>;

export const ReactivateRegulatoryDirectoryEntityRequestSchema = z
  .object({
    entityId: z.uuid(),
    reason: trimmedStringSchema.nullable().optional(),
  })
  .strict() satisfies z.ZodType<ReactivateRegulatoryDirectoryEntityRequest>;

export const ReactivateRegulatoryDirectoryEntityResponseSchema = z
  .object({
    entity: RegulatoryDirectoryEntityDetailSchema,
  })
  .strict() satisfies z.ZodType<ReactivateRegulatoryDirectoryEntityResponse>;

export const MergeRegulatoryDirectoryEntitiesRequestSchema = z
  .object({
    sourceEntityId: z.uuid(),
    targetEntityId: z.uuid(),
    reason: trimmedStringSchema.nullable().optional(),
  })
  .strict()
  .refine((value) => value.sourceEntityId !== value.targetEntityId, {
    message: 'Source and target entity IDs must be different',
  }) satisfies z.ZodType<MergeRegulatoryDirectoryEntitiesRequest>;

export const MergeRegulatoryDirectoryEntitiesResponseSchema = z
  .object({
    entity: RegulatoryDirectoryEntityDetailSchema,
  })
  .strict() satisfies z.ZodType<MergeRegulatoryDirectoryEntitiesResponse>;

export const ListRegulatoryDirectoryCandidatesRequestSchema = z
  .object({
    query: z.string().trim().optional(),
    kind: RegulatoryIdentityKindSchema.optional(),
    status: RegulatoryDirectoryCandidateStatusSchema.optional(),
    limit: limitSchema,
  })
  .strict() satisfies z.ZodType<ListRegulatoryDirectoryCandidatesRequest>;

export const ListRegulatoryDirectoryCandidatesResponseSchema = z
  .object({
    items: z.array(RegulatoryDirectoryCandidateRecordSchema),
    total: z.number().int().nonnegative(),
  })
  .strict() satisfies z.ZodType<ListRegulatoryDirectoryCandidatesResponse>;

export const GetRegulatoryDirectoryCandidateResponseSchema = z
  .object({
    candidate: RegulatoryDirectoryCandidateDetailSchema,
  })
  .strict() satisfies z.ZodType<GetRegulatoryDirectoryCandidateResponse>;

export const PromoteRegulatoryDirectoryCandidateRequestSchema = z
  .object({
    candidateId: z.uuid(),
    existingEntityId: z.uuid().optional(),
    displayName: trimmedStringSchema.optional(),
    receiverType: CompanyReceiverTypeSchema.nullable().optional(),
    addressLine1: trimmedStringSchema.nullable().optional(),
    addressLine2: trimmedStringSchema.nullable().optional(),
    city: trimmedStringSchema.nullable().optional(),
    state: trimmedStringSchema.nullable().optional(),
    postalCode: trimmedStringSchema.nullable().optional(),
    aliasValues: z.array(trimmedStringSchema).optional(),
    reason: trimmedStringSchema.nullable().optional(),
  })
  .strict() satisfies z.ZodType<PromoteRegulatoryDirectoryCandidateRequest>;

export const PromoteRegulatoryDirectoryCandidateResponseSchema = z
  .object({
    candidate: RegulatoryDirectoryCandidateDetailSchema,
    entity: RegulatoryDirectoryEntityDetailSchema,
  })
  .strict() satisfies z.ZodType<PromoteRegulatoryDirectoryCandidateResponse>;

export const ResolveRegulatoryDirectoryCandidateRequestSchema = z
  .object({
    candidateId: z.uuid(),
    entityId: z.uuid(),
    reason: trimmedStringSchema.nullable().optional(),
  })
  .strict() satisfies z.ZodType<ResolveRegulatoryDirectoryCandidateRequest>;

export const ResolveRegulatoryDirectoryCandidateResponseSchema = z
  .object({
    candidate: RegulatoryDirectoryCandidateDetailSchema,
    entity: RegulatoryDirectoryEntityDetailSchema,
  })
  .strict() satisfies z.ZodType<ResolveRegulatoryDirectoryCandidateResponse>;

export const RejectRegulatoryDirectoryCandidateRequestSchema = z
  .object({
    candidateId: z.uuid(),
    reason: trimmedStringSchema,
  })
  .strict() satisfies z.ZodType<RejectRegulatoryDirectoryCandidateRequest>;

export const RejectRegulatoryDirectoryCandidateResponseSchema = z
  .object({
    candidate: RegulatoryDirectoryCandidateDetailSchema,
  })
  .strict() satisfies z.ZodType<RejectRegulatoryDirectoryCandidateResponse>;

export const ListRegulatoryDirectoryAuditEntriesRequestSchema = z
  .object({
    entityId: z.uuid().optional(),
    candidateId: z.uuid().optional(),
    limit: limitSchema,
  })
  .strict()
  .refine((value) => value.entityId !== undefined || value.candidateId !== undefined, {
    message: 'entityId or candidateId is required for regulatory directory audit queries',
  }) satisfies z.ZodType<ListRegulatoryDirectoryAuditEntriesRequest>;

export const ListRegulatoryDirectoryAuditEntriesResponseSchema = z
  .object({
    items: z.array(RegulatoryDirectoryAuditEntryRecordSchema),
  })
  .strict() satisfies z.ZodType<ListRegulatoryDirectoryAuditEntriesResponse>;
